/**
 * @sazeo/sdk — Home widget hooks
 *
 * Minimal hooks that call the `home` Edge Function for per-user widget
 * configuration (show/hide, order, size).
 *
 * Paired spec: specs/CODEX_PHASE2_WAVE4_CALENDAR_TASKS_HOME.md §2.6 / §3.3
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type WidgetSize = 'small' | 'medium' | 'large';

export const CORE_WIDGET_KEYS = [
  'my_tasks',
  'upcoming_events',
  'inbox_summary',
  'recent_docs',
  'attendance_chart',
  'team_activity',
  'headcount_by_dept',
  'tasks_completion',
  'monthly_expenses',
] as const;

export type CoreWidgetKey = (typeof CORE_WIDGET_KEYS)[number];

export interface HomeWidgetConfigRow {
  widget_key: string;
  position: number;
  size: WidgetSize;
  config: Record<string, unknown>;
  enabled: boolean;
  updated_at: string;
}

export interface HomeWidgetUpdateInput {
  widget_key: string;
  position?: number;
  size?: WidgetSize;
  enabled?: boolean;
  config?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Internal fetch helpers
// ---------------------------------------------------------------------------

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(): SupabaseAuthLike {
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return {
      auth: {
        getSession: async () => ({ data: { session: null } }),
      },
    };
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const client = resolveClient();
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

function useAsyncQuery<T>(
  fn: () => Promise<T>,
  initial: T,
  deps: readonly unknown[],
  enabled = true,
): { data: T; isLoading: boolean; error: Error | null; refetch: () => void } {
  const [data, setData] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    fn()
      .then((d) => {
        if (mountedRef.current) setData(d);
      })
      .catch((e) => {
        if (mountedRef.current) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error, refetch: run };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Default widget layout used until the backend seeds the user's row or when
 * the Edge Function is unavailable. Keeps the Home page usable in dev.
 */
export const DEFAULT_WIDGET_CONFIG: HomeWidgetConfigRow[] = CORE_WIDGET_KEYS.map((key, index) => ({
  widget_key: key,
  position: index,
  size: 'medium',
  config: {},
  enabled: true,
  updated_at: new Date(0).toISOString(),
}));

export function useHomeWidgets() {
  return useAsyncQuery<HomeWidgetConfigRow[]>(
    async () => {
      try {
        return await apiFetch<HomeWidgetConfigRow[]>('home/widgets');
      } catch {
        // Fall back to defaults while the Edge Function is still being built
        return DEFAULT_WIDGET_CONFIG;
      }
    },
    DEFAULT_WIDGET_CONFIG,
    [],
  );
}

export function useUpdateHomeWidgets() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(async (rows: HomeWidgetUpdateInput[]) => {
    setIsPending(true);
    try {
      return await apiFetch<HomeWidgetConfigRow[]>('home/widgets', {
        method: 'PUT',
        body: JSON.stringify({ widgets: rows }),
      });
    } finally {
      setIsPending(false);
    }
  }, []);
  return { mutate, isPending };
}

export function useToggleWidget() {
  const [isPending, setIsPending] = useState(false);
  const mutate = useCallback(
    async (input: { widget_key: string; enabled?: boolean; size?: WidgetSize }) => {
      setIsPending(true);
      try {
        return await apiFetch<HomeWidgetConfigRow>(
          `home/widgets/${encodeURIComponent(input.widget_key)}`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              ...(input.enabled !== undefined ? { enabled: input.enabled } : {}),
              ...(input.size !== undefined ? { size: input.size } : {}),
            }),
          },
        );
      } finally {
        setIsPending(false);
      }
    },
    [],
  );
  return { mutate, isPending };
}
